import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Logo } from './Logo';
import { Ic } from './Icon';
import { useAuth, isAdmin } from '../store/auth';
import { roomInvitesApi } from '../api/roomInvites';
import { roomNotificationsApi } from '../api/roomNotifications';

interface NavItem {
  to: string;
  icon: React.ReactNode;
  label: string;
  exact?: boolean;
  accent?: boolean;
  badge?: number;
}

export function Sidebar() {
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const navigate = useNavigate();

  const inviteCount = useQuery({
    queryKey: ['my-room-invites-count'],
    queryFn: roomInvitesApi.count,
    refetchInterval: 300_000,
  });
  const notifCount = useQuery({
    queryKey: ['my-room-notifications-count'],
    queryFn: roomNotificationsApi.count,
    refetchInterval: 300_000,
  });
  const pendingInvites = (inviteCount.data?.count ?? 0) + (notifCount.data?.count ?? 0);

  const items: NavItem[] = [
    { to: '/', icon: <Ic.home size={18} />, label: 'Dashboard', exact: true },
    { to: '/calendar', icon: <Ic.calendar size={18} />, label: 'Mecze' },
    { to: '/rooms', icon: <Ic.users size={18} />, label: 'Pokoje', badge: pendingInvites },
    { to: '/profile', icon: <Ic.user size={18} />, label: 'Profil' },
    ...(isAdmin(user)
      ? [{ to: '/admin/matches', icon: <Ic.bolt size={18} />, label: 'Zarządzaj', accent: true } as NavItem]
      : []),
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside
      className="desktop-only"
      style={{
        width: 232,
        background: '#fff',
        borderRight: '1px solid rgba(15,30,61,.06)',
        padding: '22px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 22,
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        alignSelf: 'flex-start',
        height: '100vh',
      }}
    >
      <div style={{ padding: '0 8px' }}>
        <Logo size={60} />
      </div>

      <nav style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.exact}
            style={({ isActive }) => ({
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 12px',
              borderRadius: 10,
              background: isActive
                ? it.accent
                  ? 'rgba(244,185,58,.18)'
                  : 'rgba(15,30,61,.06)'
                : 'transparent',
              color: isActive ? (it.accent ? '#6b4a00' : '#0f1e3d') : 'rgba(15,30,61,.68)',
              fontWeight: isActive ? 600 : 500,
              fontSize: 14,
              cursor: 'pointer',
              textDecoration: 'none',
            })}
          >
            {({ isActive }) => (
              <>
                {it.icon}
                {it.label}
                {it.badge && it.badge > 0 ? (
                  <span
                    style={{
                      marginLeft: 'auto',
                      minWidth: 20,
                      height: 20,
                      padding: '0 6px',
                      borderRadius: 10,
                      background: '#d0463c',
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {it.badge}
                  </span>
                ) : isActive && !it.accent ? (
                  <span
                    style={{
                      marginLeft: 'auto',
                      width: 4,
                      height: 18,
                      borderRadius: 4,
                      background: 'var(--accent)',
                    }}
                  />
                ) : null}
              </>
            )}
          </NavLink>
        ))}

        <button
          type="button"
          onClick={handleLogout}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 12px',
            borderRadius: 10,
            marginTop: 'auto',
            background: 'transparent',
            border: 'none',
            color: '#8a2a22',
            fontWeight: 500,
            fontSize: 14,
            fontFamily: 'inherit',
            cursor: 'pointer',
            textAlign: 'left',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(208,70,60,.08)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          <Ic.logout size={18} />
          Wyloguj się
        </button>
      </nav>
    </aside>
  );
}
